import React, { useState, useEffect } from 'react';
import { Download, X, Share } from 'lucide-react';

const DISMISS_KEY = 'install_banner_dismissed_v1';

const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;

const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent);
const isMobileUA = () => /android|iphone|ipad|ipod/i.test(navigator.userAgent);

/**
 * InstallAppBanner — mobile-only "Install App" prompt.
 * Android/Chrome: captures `beforeinstallprompt` and triggers the native
 * install dialog. iOS Safari: shows a one-time hint (Share → Add to Home
 * Screen). Never renders on desktop or when already installed.
 */
export const InstallAppBanner = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!isMobileUA() || isStandalone() || localStorage.getItem(DISMISS_KEY)) return undefined;
    if (isIOS()) {
      setVisible(true);
      return undefined;
    }
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    const installed = () => setVisible(false);
    window.addEventListener('appinstalled', installed);
    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installed);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    setVisible(false);
  };

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') setVisible(false);
    setDeferredPrompt(null);
    localStorage.setItem(DISMISS_KEY, '1');
  };

  if (!visible) return null;

  return (
    <div
      className="sm:hidden fixed bottom-3 left-3 right-3 z-[1200] flex items-center gap-3 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 shadow-2xl"
      data-testid="install-app-banner"
    >
      <img src="/icon-192-maskable.png" alt="LIVETRAK" className="w-10 h-10 rounded-xl shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-foreground">Install LIVETRAK</p>
        {isIOS() ? (
          <p className="text-xs text-muted-foreground flex items-center gap-1 flex-wrap">
            Tap <Share className="w-3.5 h-3.5 inline" /> Share, then &quot;Add to Home Screen&quot;
          </p>
        ) : (
          <p className="text-xs text-muted-foreground">Get the full-screen app experience</p>
        )}
      </div>
      {!isIOS() && (
        <button
          type="button"
          onClick={install}
          className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-[#2563eb] px-3.5 py-2 text-xs font-semibold text-white"
          data-testid="install-app-btn"
        >
          <Download className="w-3.5 h-3.5" /> Install
        </button>
      )}
      <button
        type="button"
        onClick={dismiss}
        className="shrink-0 p-1.5 text-muted-foreground hover:text-foreground"
        aria-label="Dismiss install banner"
        data-testid="install-app-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};
